import React, {useState, useEffect, useRef, memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import Link from 'next/link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {cdnImage} from '@/util/cdnImage';
import {borderRadius, colors, fontSize, fontWeight, gradients, overlay, spacing} from '@/styles/tokens';
import {imgSizes} from '@/util/imageSizes';

const gurpreet = cdnImage('about-us-new/Gurpreet-singh-banner-image.webp');
const Daljit = cdnImage('about-us-new/Daljit-singh-banner-image.webp');
const Harry = cdnImage('about-us-new/Harry-banner-image.webp');

const CAROUSEL_SLIDES = [
    {
        image: gurpreet,
        name: 'Gurpreet Singh',
        designation: 'CEO & Director of AI & Emerging Technologies',
        linkedin: 'https://www.linkedin.com/in/gurpreet-singh-630b5a12'
    },
    {
        image: Daljit,
        name: 'Daljit Singh',
        designation: 'Co-founder & Director of Blockchain & AI Technology',
        linkedin: 'https://www.linkedin.com/in/daljit-singh-panesar-522b3918'
    },
    {
        image: Harry,
        name: 'Harpreet Singh',
        designation: 'Director – Digital Transformation & Customer Success',
        linkedin: 'https://www.linkedin.com/in/dhillonharry'
    }
];

// Clone of first slide appended so the track can slide forward past the last real slide
const SLIDES_WITH_CLONE = [...CAROUSEL_SLIDES, CAROUSEL_SLIDES[0]];

const AboutBanner = ({handleClickOpen}: {handleClickOpen?: () => void}) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const trackRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveSlide((prev) => prev + 1);
        }, 3500);
        return () => clearInterval(timer);
    }, []);

    // Reliable loop reset — does not depend on onTransitionEnd firing
    useEffect(() => {
        let reset: ReturnType<typeof setTimeout> | null = null;
        if (activeSlide === CAROUSEL_SLIDES.length) {
            reset = setTimeout(() => {
                const el = trackRef.current;
                if (!el) return;
                el.style.transition = 'none';
                setActiveSlide(0);
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (trackRef.current) trackRef.current.style.transition = '';
                    });
                });
            }, 520);
        }
        return () => {
            if (reset) clearTimeout(reset);
        };
    }, [activeSlide]);

    return (
        <Box
            component="section"
            sx={{
                background: gradients.banner,
                py: spacing[64]
            }}
        >
            <MUIContainer>
                <Grid container spacing={{xs: 4, md: 8}} alignItems="center">
                    {/* ── Left column ─────────────────────────────── */}
                    <Grid item xs={12} md={6}>
                        {/* Heading */}
                        <Typography
                            component="h1"
                            className="font-headers"
                            sx={{
                                fontSize: {xs: fontSize[32], sm: fontSize[40], md: fontSize[48]},
                                lineHeight: 1.2,
                                mb: 3
                            }}
                        >
                            Your Technology Partner for Digital Transformation
                        </Typography>

                        {/* Description */}
                        <Typography
                            className="font-text-regular"
                            sx={{
                                fontSize: fontSize[16],
                                lineHeight: 1.85,
                                mb: 4
                            }}
                        >
                            Since 2011, Debut Infotech has partnered with startups and enterprises to turn business ideas into scalable
                            digital solutions. We combine product engineering expertise with emerging technologies to help businesses solve
                            complex challenges and pursue new opportunities.
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                            <Button
                                variant="contained"
                                endIcon={<ArrowForwardIcon />}
                                onClick={handleClickOpen}
                                className="button-bg-primary animated-button moving-icon-button"
                                sx={{
                                    background: gradients.primaryButton,
                                    color: colors.text.inverse,
                                    borderRadius: borderRadius.md,
                                    px: 3,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: fontWeight.semibold,
                                    fontSize: fontSize[16],
                                    boxShadow: 'none',
                                    '&:hover': {
                                        background: 'transparent !important',
                                        boxShadow: 'none',
                                        border: `1.5px solid ${colors.text.primary}`,
                                        color: colors.text.primary
                                    }
                                }}
                            >
                                Discuss Your Project
                            </Button>
                            <Button
                                variant="outlined"
                                component={Link}
                                className="font-headers"
                                href="/portfolio"
                                sx={{
                                    border: `1.5px solid ${colors.neutral.slate300}`,
                                    color: colors.text.primary,
                                    borderRadius: borderRadius.md,
                                    px: 3,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: fontWeight.semibold,
                                    fontSize: fontSize[16],
                                    background: colors.background.default,
                                    '&:hover': {background: 'transparent', border: `1.5px solid ${colors.neutral.slate400}`}
                                }}
                            >
                                View Our Portfolio
                            </Button>
                        </Box>
                    </Grid>

                    {/* ── Right column — carousel ──────────────────── */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                borderRadius: borderRadius['2xl'],
                                overflow: 'hidden',
                                position: 'relative',
                                aspectRatio: '4 / 3'
                            }}
                        >
                            {/* Sliding track */}
                            <Box
                                ref={trackRef}
                                sx={{
                                    display: 'flex',
                                    height: '100%',
                                    transform: `translateX(-${activeSlide * 100}%)`,
                                    transition: 'transform 0.5s ease'
                                }}
                            >
                                {SLIDES_WITH_CLONE.map((slide, i) => (
                                    <Box key={i} sx={{position: 'relative', minWidth: '100%', height: '100%'}}>
                                        {/* Carousel is full-width on mobile, half the 1200px container on md+. */}
                                        <Image
                                            src={slide.image}
                                            alt={slide.name}
                                            fill
                                            sizes={imgSizes({xs: '100vw', md: '600px'})}
                                            style={{objectFit: 'cover'}}
                                            priority={i === 0}
                                        />
                                    </Box>
                                ))}
                            </Box>

                            {/* Gradient overlay + name */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: `linear-gradient(to top, ${colors.text.primary} 0%, transparent 100%)`,
                                    pt: 10,
                                    pb: 2.5,
                                    px: 3
                                }}
                            >
                                <Typography
                                    className="font-headers"
                                    sx={{color: colors.text.inverse, fontWeight: fontWeight.bold, fontSize: fontSize[20], lineHeight: 1.3}}
                                >
                                    <Link
                                        href={CAROUSEL_SLIDES[activeSlide % CAROUSEL_SLIDES.length].linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{color: 'inherit', textDecoration: 'none'}}
                                    >
                                        {CAROUSEL_SLIDES[activeSlide % CAROUSEL_SLIDES.length].name}
                                    </Link>
                                </Typography>
                                <Typography
                                    className="font-text-regular"
                                    sx={{color: overlay.whiteSubtitle, fontSize: fontSize[16], mt: 0.25}}
                                >
                                    {CAROUSEL_SLIDES[activeSlide % CAROUSEL_SLIDES.length].designation}
                                </Typography>

                                {/* Dots */}
                                <Box sx={{display: 'flex', gap: 0.75, mt: 1.75, justifyContent: 'center'}}>
                                    {CAROUSEL_SLIDES.map((_, i) => {
                                        const isActive = i === activeSlide % CAROUSEL_SLIDES.length;
                                        return (
                                            <Box
                                                key={i}
                                                onClick={() => setActiveSlide(i)}
                                                sx={{
                                                    width: isActive ? 10 : 8,
                                                    height: isActive ? 10 : 8,
                                                    borderRadius: borderRadius.circle,
                                                    background: isActive ? colors.accent.orange500 : overlay.whiteDim,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.25s ease',
                                                    '&:hover': {background: isActive ? colors.accent.orange500 : overlay.whiteSoft}
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(AboutBanner);
